import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParivar } from '../../context/ParivarContext';
import { newsEventApi } from '../../api/newsEvents';
import { mediaUrl } from '../../api/client';
import defaultParivarImage from '../../assets/parivar-default-devotional.webp';
import Reveal from '../Reveal/Reveal';
import './YourParivarCard.scss';

function YourParivarCard() {
  const { ready, selectedParivar } = useParivar();
  const [upcoming, setUpcoming] = useState(null);
  const [imgErrored, setImgErrored] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!ready || !selectedParivar) {
      setUpcoming(null);
      return undefined;
    }
    newsEventApi
      .list({ type: 'event', parivar_id: selectedParivar.id, page_size: 100 })
      .then((json) => {
        if (!mounted) return;
        const rows = json.results || json || [];
        const today = new Date().toISOString().slice(0, 10);
        const next = rows
          .filter((e) => e.date && e.date >= today)
          .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0] || null;
        setUpcoming(next);
      })
      .catch(() => {
        if (mounted) setUpcoming(null);
      });
    return () => {
      mounted = false;
    };
  }, [ready, selectedParivar]);

  useEffect(() => {
    setImgErrored(false);
  }, [selectedParivar?.image_url]);

  if (!ready || !selectedParivar) return null;

  const name = selectedParivar.name || '';
  const city = selectedParivar.city || '';
  const state = selectedParivar.state || '';
  const place = [city, state].filter(Boolean).join(', ');

  const uploadedImage = mediaUrl(selectedParivar.image_url);
  const parivarImage = !imgErrored && uploadedImage ? uploadedImage : defaultParivarImage;

  const formatDate = (iso) => {
    if (!iso) return '';
    const utc = new Date(String(iso) + (String(iso).length === 10 ? 'T00:00:00' : ''));
    return utc.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const formatTime = (value) => {
    if (!value) return '';
    const [h, m] = String(value).split(':');
    const hour = parseInt(h, 10);
    const min = m || '00';
    const suffix = hour >= 12 ? 'PM' : 'AM';
    return ((hour % 12) || 12) + ':' + min + ' ' + suffix;
  };
  const timingRange = (start, end) => {
    const parts = [formatTime(start), formatTime(end)].filter(Boolean);
    return parts.length ? parts.join(' - ') : '';
  };

  const morningRange = timingRange(
    selectedParivar.darshan_morning_start,
    selectedParivar.darshan_morning_end
  );
  const eveningRange = timingRange(
    selectedParivar.darshan_evening_start,
    selectedParivar.darshan_evening_end
  );

  return (
    <section className="your-parivar-section" aria-label="Your Parivar">
      <div className="container">
        <Reveal direction="up">
          <div className="your-parivar-card">
            <aside className="your-parivar-media" aria-hidden="true">
              <img
                className="your-parivar-media-img"
                src={parivarImage}
                alt=""
                loading="lazy"
                onError={() => setImgErrored(true)}
              />
            </aside>

            <div className="your-parivar-body">
              <div className="your-parivar-head">
                <h2 className="your-parivar-name">{name}</h2>
                {place && (
                  <p className="your-parivar-location">
                    <i className="fas fa-map-marker-alt" aria-hidden="true"></i> {place}
                  </p>
                )}
              </div>

              <div className="your-parivar-panels">
                {upcoming ? (
                  <div className="your-parivar-panel">
                    <span className="your-parivar-panel-icon" aria-hidden="true">
                      <i className="fas fa-calendar"></i>
                    </span>
                    <div className="your-parivar-panel-content">
                      <span className="your-parivar-panel-label">Upcoming Local Event</span>
                      <strong className="your-parivar-panel-main">{upcoming.title}</strong>
                      <span className="your-parivar-panel-meta">{formatDate(upcoming.date)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="your-parivar-panel is-empty">
                    <span className="your-parivar-panel-icon" aria-hidden="true">
                      <i className="fas fa-calendar-day"></i>
                    </span>
                    <div className="your-parivar-panel-content">
                      <span className="your-parivar-panel-label">Upcoming Local Event</span>
                      <span className="your-parivar-panel-meta">No local events planned yet</span>
                    </div>
                  </div>
                )}

                {morningRange && (
                  <div className="your-parivar-panel">
                    <span className="your-parivar-panel-icon" aria-hidden="true">
                      <i className="fas fa-sun"></i>
                    </span>
                    <div className="your-parivar-panel-content">
                      <span className="your-parivar-panel-label">Morning Darshan</span>
                      <strong className="your-parivar-panel-main">{morningRange}</strong>
                    </div>
                  </div>
                )}

                {eveningRange && (
                  <div className="your-parivar-panel">
                    <span className="your-parivar-panel-icon" aria-hidden="true">
                      <i className="fas fa-moon"></i>
                    </span>
                    <div className="your-parivar-panel-content">
                      <span className="your-parivar-panel-label">Evening Darshan</span>
                      <strong className="your-parivar-panel-main">{eveningRange}</strong>
                    </div>
                  </div>
                )}
              </div>

              <Link to={'/parivar/' + (selectedParivar.slug || '')} className="your-parivar-cta">
                View Parivar Details <i className="fas fa-arrow-right" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default YourParivarCard;